import has from 'lodash/has';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';

import { decodeToArrayBuffer, encodeArrayBuffer, isWebAuthnSupported } from './webauthn';

type BiometricLoginChallengeJson = {
  publicKey: PublicKeyCredentialRequestOptions;
};

const parseBiometricChallenge = (str: string): BiometricLoginChallengeJson => {
  const parsed = JSON.parse(str);

  const isValid =
    isPlainObject(parsed) && has(parsed, 'publicKey.challenge') && has(parsed, 'publicKey.allowCredentials');

  if (!isValid) {
    throw new Error('Invalid biometric challenge structure');
  }

  return parsed as BiometricLoginChallengeJson;
};

const preparePublicKeyOptions = (challenge: BiometricLoginChallengeJson): PublicKeyCredentialRequestOptions => {
  const { publicKey } = challenge;

  return {
    ...publicKey,
    challenge: decodeToArrayBuffer(publicKey.challenge as unknown as string),
    allowCredentials: publicKey.allowCredentials?.map(c => ({
      ...c,
      id: isString(c.id) ? decodeToArrayBuffer(c.id as unknown as string) : c.id,
    })),
  };
};

export const getBiometricChallengeResponse = async (challengeStr: string): Promise<string> => {
  if (!isWebAuthnSupported()) {
    throw new Error('WebAuthn API is not supported in this browser');
  }

  const challenge = parseBiometricChallenge(challengeStr);
  const publicKey = preparePublicKeyOptions(challenge);

  const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error('Failed to retrieve public key credential');
  }

  const response = credential.response as AuthenticatorAssertionResponse;

  return JSON.stringify({
    id: credential.id,
    type: 'public-key',
    rawId: encodeArrayBuffer(credential.rawId),
    response: {
      authenticatorData: encodeArrayBuffer(response.authenticatorData),
      clientDataJSON: encodeArrayBuffer(response.clientDataJSON),
      signature: encodeArrayBuffer(response.signature),
    },
  });
};
